import { useMutation, useQueryClient } from '@tanstack/react-query';
import { manageApi } from '../api/manageApi';
import { manageQueryKeys } from './queryKeys';
import { InviteAdminDTO } from '../dtos';
import { AdminUserTableData, UserRole } from '../types';
import { notificationService } from '@shared/components/notifications';
import { useAuthContext } from '@shared/components';
import { userInvitationApi, InviteUserWithRoleDTO } from '../../user-invitation';
import { useUsersWithRoles, useUserExists, userManagementQueryKeys } from '../../user-management';
import type { UserWithRoleDTO } from '../../user-management';

// Query hook for fetching system admin users - NOW USES CENTRALIZED API
export const useSystemAdmins = () => {
  return useUsersWithRoles(
    {
      role: UserRole.SYSTEM_ADMIN,
    },
    {
      staleTime: 3 * 60 * 1000, // 3 minutes (admin users change less frequently)
    }
  );
};

// Query hook for checking if a user exists - NOW USES CENTRALIZED API
export const useCheckUserExists = (email: string) => {
  return useUserExists(email, !!email && email.includes('@')); // Only run if email is valid
};

// Mutation hook for inviting admin user - UPDATED to use unified API
export const useInviteAdmin = () => {
  const queryClient = useQueryClient();
  const { getCurrentUserId } = useAuthContext();

  return useMutation({
    mutationFn: async (data: InviteAdminDTO) => {
      // Convert old DTO to new unified DTO
      const unifiedData: InviteUserWithRoleDTO = {
        email: data.email,
        name: data.name,
        role: 'SYSTEM_ADMIN',
        tenantId: data.tenantId,
        assignedBy: getCurrentUserId(),
      };

      const response = await userInvitationApi.inviteUserWithRole(unifiedData);
      return response.data;
    },
    onSuccess: (result, variables) => {
      // Invalidate legacy manage queries
      queryClient.invalidateQueries({ queryKey: manageQueryKeys.systemAdmins });

      // Invalidate user existence check for this email
      queryClient.invalidateQueries({
        queryKey: manageQueryKeys.userExists(variables.email),
      });

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byRole('SYSTEM_ADMIN'),
      });
      if (variables.tenantId) {
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.byTenant(variables.tenantId),
        });
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.usersWithRoles({
            tenantId: variables.tenantId,
            role: UserRole.SYSTEM_ADMIN,
          }),
        });
      }

      const message = result?.message || `Admin invitation sent to ${variables.email}`;
      notificationService.success(message);
    },
    onError: (error: any, variables) => {
      const errorMessage = error?.message || `Failed to invite admin user ${variables.email}`;
      notificationService.error(errorMessage);
    },
  });
};

// Mutation hook for removing admin role
export const useRemoveAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      tenantId,
      userRoleId,
      userName,
    }: {
      userId: string;
      tenantId: string;
      userRoleId: string;
      userName?: string;
    }) => {
      await manageApi.admin.removeAdminRole(userId, tenantId, userRoleId);
      return { userId, userName };
    },
    onSuccess: ({ userName }, variables) => {
      // Invalidate legacy manage queries
      queryClient.invalidateQueries({ queryKey: manageQueryKeys.systemAdmins });

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byRole('SYSTEM_ADMIN'),
      });
      if (variables.tenantId) {
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.byTenant(variables.tenantId),
        });
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.usersWithRoles({
            tenantId: variables.tenantId,
            role: UserRole.SYSTEM_ADMIN,
          }),
        });
      }

      const message = userName
        ? `Admin role removed from ${userName}`
        : 'Admin role removed successfully';

      notificationService.success(message);
    },
    onError: (error: any, { userName }) => {
      const baseMessage = userName
        ? `Failed to remove admin role from ${userName}`
        : 'Failed to remove admin role';

      const errorMessage = error?.message || baseMessage;
      notificationService.error(errorMessage);
    },
  });
};

// Computed hook for transforming admin user data for table display - NOW SIMPLIFIED
export const useSystemAdminsTableData = () => {
  const { data: adminUsers, ...queryResult } = useSystemAdmins();

  const tableData: AdminUserTableData[] = (adminUsers || []).map(
    (userWithRole: UserWithRoleDTO) => {
      // Check if user has completed registration (has cognitoId)
      const status = userWithRole.cognitoId ? 'active' : 'invited';

      // Get the first role (since we're filtering by SYSTEM_ADMIN, there should be one relevant role)
      const adminRole = userWithRole.roles?.find((role) => role.role === UserRole.SYSTEM_ADMIN);

      return {
        id: `${userWithRole.id}-${adminRole?.userRoleId}`, // Composite ID for table
        name: userWithRole.name || 'Pending Registration',
        email: userWithRole.email || 'unknown@example.com',
        role: adminRole?.role || 'SYSTEM_ADMIN',
        assignedAt: adminRole?.assignedAt || '',
        userRoleId: adminRole?.userRoleId || '',
        userId: userWithRole.id,
        status, // Add status to track invitation state
      };
    }
  );

  return {
    data: tableData,
    ...queryResult,
  };
};

// REMOVED: useSystemAdminsWithUserDetails hook - No longer needed!
// The new centralized API returns complete user data in one call,
// eliminating the need for complex Promise.all chains to fetch user details.
// Use useSystemAdminsTableData() instead which now gets complete data directly.
